import { registerLocaleData } from '@angular/common';
import localeCa from '@angular/common/locales/ca';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import localeEu from '@angular/common/locales/eu';
import localeFr from '@angular/common/locales/fr';
import localeGl from '@angular/common/locales/gl';
import localeIt from '@angular/common/locales/it';
import localeNl from '@angular/common/locales/nl';
import localePt from '@angular/common/locales/pt';
import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { ListDropdownService } from '@vueling-ng/vy-ds/src/lib/components/composed/header';
import { InternationalizationEnvironment } from 'app/core/configuration';
import { EnabledSessionsStorageKeys } from 'app/shared/models/configs-sessions-storage';
import { AppState } from 'app/shared/state/models/app-state.model';
import { StateManagerService } from 'app/shared/state/state-manager.service';
import { map } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly _translateService = inject(TranslateService);
  private readonly _stateManagerService = inject(StateManagerService);
  private readonly _listDropdownService = inject(ListDropdownService);

  public currentLanguage = toSignal(
    this._translateService.onLangChange.pipe(map((event: LangChangeEvent) => event.lang)),
    { initialValue: this._translateService.currentLang }
  );

  public initialize(config: InternationalizationEnvironment, currentLang: string): void {
    const currentSession = JSON.parse(sessionStorage.getItem(EnabledSessionsStorageKeys.state) ?? '{}') as AppState;
    this._translateService.onLangChange.subscribe((value: LangChangeEvent) => {
      this.updateAuxiliaryStructures(value.lang);
    });

    registerLocaleData(localeFr);
    registerLocaleData(localeEn);
    registerLocaleData(localeEs);
    registerLocaleData(localeCa);
    registerLocaleData(localeNl);
    registerLocaleData(localeIt);
    registerLocaleData(localeDe);
    registerLocaleData(localeGl);
    registerLocaleData(localeEu);
    registerLocaleData(localePt);

    this._translateService.addLangs(config.languages);
    this._translateService.setDefaultLang(config.defaultLanguage);

    if (currentSession?.language) {
      this._translateService.use(currentSession.language);
      return;
    }
    this._translateService.use(currentLang);
  }

  private updateAuxiliaryStructures(language: string): void {
    this._stateManagerService.update((draft) => {
      draft.language = language;
    });

    this._listDropdownService.sendLanguage({
      value: language,
      label: `ds.common.languages.${language}`,
      labelSuffix: language
    });
  }

  public forceTemporaryLanguage(language: string): void {
    this._translateService.use(language);
  }
}
